import React from 'react';
import { Link } from 'react-router-dom';
import './OriginPage.css';

function OriginPage() {
  const bannerStyle = {
    backgroundImage: `url(/assets/NeXusNeuroClinical_chro.webp)`,
  };

  return (
    <main className="origin-page">
      <section className="origin-banner" style={bannerStyle}>
        <div className="origin-banner-overlay">
          <Link to="/" className="origin-back">&larr; Home</Link>
          <img
            src="/assets/nexus_neuro_logo.webp"
            alt="NeXus NeuroInformatics, Inc."
            className="origin-banner-logo"
          />
          <h1 className="origin-banner-title">Our Origin</h1>
        </div>
      </section>

      <article className="origin-body">

        <section className="origin-section">
          <span className="origin-label">Chapter One</span>
          <h2 className="origin-h2">The Bedside</h2>
          <p>
            The architecture that became NeXus did not start in a lab. It started
            at the bedside. The founder was a biochemist working as a
            Certified Nursing Assistant in end-of-life care and psychiatric wards,
            watching cortisol, oxytocin, serotonin, and dopamine play out in the
            bodies of real patients at the moments those molecules mattered most.
          </p>
          <p>
            What she saw was not abstract data. It was a patient&rsquo;s grip
            tightening when the cortisol spike came an hour after a missed dose.
            It was oxytocin returning to a face that had been blank for weeks.
            It was the moment a patient&rsquo;s biochemistry decided whether they
            would fight, trust, or find peace.
          </p>
        </section>

        <section className="origin-section">
          <span className="origin-label">Chapter Two</span>
          <h2 className="origin-h2">The Conviction</h2>
          <p>
            The bedside revealed a hypothesis that no textbook had stated plainly:
            biochemistry is deterministic by nature. The same molecule, at the
            same concentration, in the same physiological context, behaves the
            same way. What looks like unpredictability is usually missing
            context, not missing rule.
          </p>
          <p>
            If that is true, then much of what we currently learn only through
            laboratory experimentation or human study could in principle be
            modeled, simulated, and reasoned about <em>in silico</em> before a
            single test tube or trial subject is required.
          </p>
        </section>

        <section className="origin-section">
          <span className="origin-label">Chapter Three</span>
          <h2 className="origin-h2">The Architecture</h2>
          <p>
            The answer the founder built is AETHER (Adaptive Endocrine Transformer
            Heads with Emotional Regulation): a transformer architecture in which
            the same pharmacokinetic principles that govern how drugs are absorbed,
            distributed, metabolized, and eliminated also govern how the model
            assigns significance to experience over time.
          </p>
          <p>
            AETHER is the substrate. Every learned fact carries its provenance
            (who taught it, in what context, with what neurochemical state),
            written to an auditable file. Post-deployment updates traverse a
            compiler-enforced verification gate. The architecture was designed
            for review, not for opacity.
          </p>
        </section>

        <section className="origin-section">
          <span className="origin-label">Chapter Four</span>
          <h2 className="origin-h2">The Structure</h2>
          <p>
            AETHER is owned by <a href="https://nexusconcordat.com">Nexus
            Concordat, Inc.</a>, the parent corporation. Two operating subsidiaries
            carry the technology into the world: <strong>NeXus NeuroInformatics,
            Inc.</strong> for biomedical research and regulatory engagement, and{' '}
            <strong>Dynamic Hallucination, Inc.</strong> for consumer entertainment.
          </p>
          <p>
            The unusual arrangement, biomedical research alongside an entertainment
            studio, exists because the two need each other. The entertainment
            platform generates structured behavioral training data inside a
            governed simulation environment. NeXus uses that data to train its
            biomedical AI. Two products, one engine, one auditable lineage.
          </p>
        </section>

        <section className="origin-section">
          <span className="origin-label">Chapter Five</span>
          <h2 className="origin-h2">The Public Record</h2>
          <p>
            Nexus Concordat filed public comment on FDA Docket{' '}
            <strong>FDA-2026-N-4390</strong> (AI-Enabled Optimization of
            Early-Phase Clinical Trials Pilot Program) on April 29, 2026,
            accompanied by a methods paper titled <em>&ldquo;Mechanistic
            In-Silico Trial Emulation: A Third Evidentiary Substrate for
            Real-World Evidence.&rdquo;</em>
          </p>
          <p>
            Four U.S. provisional patent applications cover the architecture.
            The next chapters are being written with researchers, regulatory
            advisors, and institutional partners willing to evaluate{' '}
            <em>in silico</em> simulation as a third evidentiary substrate
            alongside randomized controlled trials and claims-based real-world
            evidence.
          </p>
          <p>
            If your work intersects with any of that,{' '}
            <Link to="/contact">reach the office of the CEO</Link>.
          </p>
        </section>

      </article>
    </main>
  );
}

export default OriginPage;
